/** @format */

import React, { useState, useEffect, useContext } from "react";
import { useHistory, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import DeleteModal from "./DeleteModal";
import SpinnerLoading from "../Common/SpinnerLoading";
import * as dateHelper from "../../Helpers/DateHelper";
import { posServices } from "../../Services";
import ListAdminButtons from "../Common/ListAdminButtons";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { AuthContext } from "../../Contexts/AuthContext";
import { globalServices } from "../../Services";
import { roles } from "../../Config/Roles";
import Tooltip from "../Common/Tooltip";
import AsyncSelect from "react-select/async";
import XLSX from "xlsx";

const Index = () => {
  const history = useHistory();
  const { userId, roleId } = useContext(AuthContext);
  const { master, admin, support, owner, cashier, accountant, manager } = roles;
  const { t: translator } = useTranslation();
  const {
    updateLocName,
    updateLocPos,
    updateLocTenant,
    updateLocStore,
    updateLocTab,
    updateLocKey,
    isMobile,
    locTenant,
  } = useContext(GlobalContext);

  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0,
  });

  const [stores, setStores] = React.useState();
  const [posList, setPosList] = useState([]);
  const [update, setUpdate] = useState(new Date());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    tenantId:
      Number(localStorage.getItem("tenantId")) > 0
        ? Number(localStorage.getItem("tenantId"))
        : null,
    storeId: 0,
    searchText: "",
    isDeactivated: false,
    sortName: "InventoryNumber",
    isAsc: true,
  });
  const [tenantObj, setTenantObj] = useState({
    id: 0,
    label: translator("Select-Tenant"),
    value: translator("Select-Tenant"),
  });

  const [storeObj, setStoreObj] = useState({
    id: null,
    label: null,
  });

  React.useEffect(() => {
    updateLocName("pos");
    updateLocTenant(0);
    updateLocStore(0);
    updateLocPos(0);
    updateLocTab(0);
    updateLocKey(0);
  }, []);

  useEffect(() => {
    if (filters.tenantId > 0) {
      globalServices
        .getStoresByTenantId(filters.tenantId)
        .then((data) => {
          setStores(data.data);
          setIsLoading(false);
        })
        .catch(() => history.push("/Error"));
    } else {
      setStores();
    }

    posServices
      .getSearch({
        tenantId: Number(filters.tenantId),
        storeId: Number(filters.storeId),
        searchText: filters.searchText,
        isDeactivated: filters.isDeactivated,
        sortName: filters.sortName,
        isAsc: filters.isAsc,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      })
      .then((res) => {
        setPosList(
          res.data.posDevices.map((p) => {
            return {
              ...p,
              activationDate: dateHelper.formatUtcToDate(p.activationDate),
              subscriptionValidTo: dateHelper.formatUtcToDate(
                p.subscriptionValidTo
              ),
              lastSyncTime: dateHelper.formatUtcToTime(p.lastSyncTime),
            };
          })
        );
        setPagination({ ...pagination, totalCount: res.data.totalCount });
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err.response);
      });
  }, [update, filters]);

  const promiseOptions = (inputValue) => {
    return globalServices
      .getTenants(
        Number(localStorage.getItem("tenantId")) > 0 ? userId : null,
        inputValue,
        Number(locTenant)
      )
      .then((res) => {
        var result = res.data.map((e) => ({
          id: e.id,
          label: e.name,
          value: e.name,
        }));
        result.unshift({
          id: 0,
          label: translator("SelectTenant-Choose"),
          oib: "",
        });
        return result;
      })
      .catch((err) => err);
  };

  const handleToAdd = () => {
    history.push("/add-pos");
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setPagination({ ...pagination, pageIndex: 0 });
    setUpdate(new Date());
  };

  const handleToUpdate = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/edit-pos/${selectedItems}`);
  };

  const handleToDetail = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/pos/${selectedItems}`);
  };

  const handleInputChange = (event) => {
    setIsLoading(true);
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value });
    setPagination({ ...pagination, pageIndex: 0 });
  };

  const handleHideDeleteModal = () => setShowDeleteModal(false);

  const confirmDelete = () => {
    let deletePromises = [];

    selectedItems.forEach((x) => {
      deletePromises.push(posServices.deletePos(x));
    });

    Promise.all(deletePromises).then((data) => {
      setUpdate(new Date());
      setShowDeleteModal(false);
      setSelectedItems([]);
    });
  };

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedItems([...selectedItems, row.posid]);
    } else {
      setSelectedItems(selectedItems.filter((p) => p !== row.posid));
    }
  };

  const handleOnSelectAll = (isSelect) => {
    if (isSelect) {
      setSelectedItems(posList.map((x) => x.posid));
    } else {
      setSelectedItems([]);
    }
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setIsLoading(true);
    setPagination({
      ...pagination,
      pageIndex: sizePerPage != pagination.pageSize ? 0 : page - 1,
      pageSize: sizePerPage,
    });
    setUpdate(new Date());
  };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      if (e.target.cellIndex) {
        history.push(`/pos/${row.posid}`);
      }
    },
  };

  const sorting = (sortName) => {
    setFilters({ ...filters, sortName, isAsc: !filters.isAsc });
  };

  function rowStyleSuspended(row, rowIdx) {
    return {
      backgroundColor: row.isSuspended ? "#fcaeae" : "inherit",
    };
  }

  const RemoteAll = ({ data, page, sizePerPage, totalSize, onTableChange }) => (
    <div>
      <BootstrapTable
        remote
        keyField="posid"
        data={data}
        columns={columns}
        rowEvents={rowEvents}
        noDataIndication={translator("No-Data")}
        rowClasses="boostrap-table-row"
        rowStyle={rowStyleSuspended}
        pagination={paginationFactory({
          page,
          sizePerPage,
          totalSize,
        })}
        onTableChange={onTableChange}
      />
    </div>
  );

  function actionFormatter(cell, row) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={selectedItems.find((x) => x === row.posid)}
            onChange={(e) => handleOnSelect(row, e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  function actionHeaderFormatter(cell, rows) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            name="workingMode"
            checked={
              posList &&
              posList.length > 0 &&
              !posList
                .map((p) => p.posid)
                .some((p) => selectedItems.indexOf(p) < 0)
            }
            onChange={(e) => handleOnSelectAll(e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  const columns = [
    {
      dataField: "posid",
      text: "",
      headerStyle: (colum, colIndex) => {
        return { width: "50px", textAlign: "center" };
      },
      headerFormatter: actionHeaderFormatter,
      formatter: actionFormatter,
    },
    {
      dataField: "inventoryNumber",
      text: translator("POS-List-TableHeader-Ict"),
      headerEvents: { onClick: () => sorting("InventoryNumber") },
      sort: true,
      sortCaret: (order, column) => {
        if (filters.sortName !== "InventoryNumber")
          return <i className="fa fa-fw fa-sort"></i>;
        if (filters.sortName === "InventoryNumber") {
          return filters.isAsc !== true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
    },
    {
      dataField: "posserialNumber",
      text: translator("POS-List-TableHeader-SerialNumber"),
      headerEvents: { onClick: () => sorting("PosSerialNumber") },
      sort: true,
      sortCaret: (order, column) => {
        if (filters.sortName !== "PosSerialNumber")
          return <i className="fa fa-fw fa-sort"></i>;
        if (filters.sortName === "PosSerialNumber") {
          return filters.isAsc === true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
    {
      dataField: "tag",
      text: translator("POS-List-TableHeader-Tag"),
      headerEvents: { onClick: () => sorting("Tag") },
      sort: true,
      sortCaret: (order, column) => {
        if (filters.sortName !== "Tag")
          return <i className="fa fa-fw fa-sort"></i>;
        if (filters.sortName === "Tag") {
          return filters.isAsc === true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
    {
      dataField: "activationDate",
      text: translator("POS-List-TableHeader-ActivationDate"),
      headerEvents: { onClick: () => sorting("ActivationDate") },
      sort: true,
      sortCaret: (order, column) => {
        if (filters.sortName !== "ActivationDate")
          return <i className="fa fa-fw fa-sort"></i>;
        if (filters.sortName === "ActivationDate") {
          return filters.isAsc === true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
    {
      dataField: "lastSyncTime",
      text: translator("POS-POSDetails-LastSync"),
      headerEvents: { onClick: () => sorting("LastSyncTime") },
      sort: true,
      sortCaret: (order, column) => {
        if (filters.sortName !== "LastSyncTime")
          return <i className="fa fa-fw fa-sort"></i>;
        if (filters.sortName === "LastSyncTime") {
          return filters.isAsc === true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
    {
      dataField: "posVersion",
      text: translator("POS-List-TableHeader-PosVersion"),
      headerEvents: { onClick: () => sorting("posVersion") },
      sort: false,
      sortCaret: (order, column) => {
        if (filters.sortName !== "posVersion")
          return <i className="fa fa-fw fa-sort"></i>;
        if (filters.sortName === "posVersion") {
          return filters.isAsc === true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
  ];

  const exportExcelFunction = () => {
    posServices
      .getSearch({
        tenantId: Number(filters.tenantId),
        storeId: Number(filters.storeId),
        searchText: filters.searchText,
        isDeactivated: filters.isDeactivated,
        sortName: filters.sortName,
        isAsc: filters.isAsc,
        pageIndex: pagination.pageIndex,
        pageSize: 0,
      })
      .then((res) => {
        console.log(res);

        // File name
        var ws_name = `pos.xlsx`;
        // New work book
        var wb = XLSX.utils.book_new();
        // Constant Data
        var ws_data = [
          [
            translator("POS-View-Tenant"),
            tenantObj.id === 0 ? null : tenantObj.label,
          ],
          [translator("POS-List-Store"), storeObj.label],
          [translator("Search-Text"), filters.searchText],
          [],
          [
            translator("POS-List-TableHeader-Ict"),
            translator("POS-List-TableHeader-Msisdn"),
            translator("POS-List-TableHeader-SerialNumber"),
            translator("POS-List-TableHeader-Tag"),
            translator("POS-List-TableHeader-ActivationDate"),
          ],
        ];

        // Inconsistent data / Reports

        const ws_sufix = res.data.posDevices.map((x) => {
          const array = [
            x.inventoryNumber,
            x.msisdn,
            x.posserialNumber,
            x.tag,
            dateHelper.formatUtcToDate(x.activationDate),
            dateHelper.formatUtcToYYYYMMDDHH_24MMSS(x.lastSyncTime),
          ];
          return array;
        });
        // Merge data
        ws_data = [...ws_data, ...ws_sufix];

        // Export to xlsx
        const ws = XLSX.utils.aoa_to_sheet(ws_data);

        // console.log(ws);
        ws["!cols"] = [
          { width: 22 },
          { width: 20 },
          { width: 20 },
          { width: 20 },
          { width: 20 },
        ];
        XLSX.utils.book_append_sheet(wb, ws, ws_name);
        XLSX.writeFile(wb, ws_name);
      })
      .catch((err) => {
        console.error(err.response);
      });
  };

  return (
    <div className="p-2">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("POS-List-pageTitle")} </span>
          <span className="badge badge-pill badge-primary">
            {" "}
            {!isLoading ? pagination.totalCount : null}
          </span>
          {[master, admin, support].includes(Number(roleId)) && (
            <button
              className={
                "btn btn-primary float-right" + (isMobile ? " btn-sm" : "")
              }
              onClick={handleToAdd}
            >
              <span
                className="icon icon-cancel web-hidden"
                style={{ transform: "rotate(45deg)" }}
              ></span>{" "}
              <span className="mob-hidden">
                {translator("POS-List-labelAddPOS")}
              </span>
            </button>
          )}
        </div>
        <div className="card-body">
          <fieldset className="scheduler-border">
            <legend className="scheduler-border">
              {translator("POS-List-labelFilters")}
            </legend>
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              {localStorage.getItem("tenantId") === "null" && (
                <div className="form-group col-lg-3">
                  <label htmlFor="ddlStore">
                    {translator("POS-View-Tenant")}
                  </label>
                  <AsyncSelect
                    name="tenantId"
                    onChange={(event) => {
                      setIsLoading(true);
                      setTenantObj({
                        id: event.id,
                        label: event.label,
                        value: event.value,
                      });
                      setFilters({ ...filters, tenantId: event.id });
                      setPagination({ ...pagination, pageIndex: 0 });
                    }}
                    defaultOptions={true}
                    loadOptions={promiseOptions}
                    placeholder={translator("Select-Tenant")}
                    value={tenantObj}
                  />
                </div>
              )}

              <div className="form-group col-lg-3">
                <label htmlFor="ddlStore">{translator("POS-List-Store")}</label>
                <select
                  name="storeId"
                  className="form-control"
                  onChange={(event) => {
                    setIsLoading(true);
                    const { name, value, selectedIndex } = event.target;
                    setStoreObj({
                      id: value,
                      label: event.target[selectedIndex].text,
                    });
                    setFilters({ ...filters, [name]: value });
                    setPagination({ ...pagination, pageIndex: 0 });
                  }}
                  disabled={!stores}
                >
                  <option value="0">
                    {translator("POS-List-StoreSelect-All")}
                  </option>
                  {stores &&
                    stores.map((s) => <option value={s.id}>{s.name}</option>)}
                </select>
              </div>

              <div className="form-group col-lg-3">
                <label>{translator("POS-List-labelTag")}</label>{" "}
                <Tooltip text={translator("POSTooltip-Msg")} />
                <input
                  name="searchText"
                  type="text"
                  className="form-control"
                  defaultValue={filters.searchText}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group col-lg-2 col-md-4 col-xs-12">
                <label className="labelCont mt-5">
                  <input
                    type="checkbox"
                    name="cashDefault"
                    checked={filters.isDeactivated}
                    onChange={(event) => {
                      setIsLoading(true);
                      setFilters({
                        ...filters,
                        isDeactivated: event.target.checked,
                      });
                      setPagination({ ...pagination, pageIndex: 0 });
                    }}
                  />
                  <span className="checkmark"></span>
                </label>
                <span>{translator("POS-ShowDeactivated")}</span>
              </div>
            </form>
          </fieldset>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <ListAdminButtons
              selectedItemsLength={selectedItems.length}
              handleToDetail={handleToDetail}
              handleToUpdate={
                ![accountant, cashier, manager].includes(Number(roleId))
                  ? handleToUpdate
                  : null
              }
              handleDelete={
                ![owner, accountant, cashier, manager].includes(Number(roleId))
                  ? () => setShowDeleteModal(true)
                  : null
              }
              handleRefresh={handleRefresh}
            />
            {/* <button
							className='btn btn-outline-secondary my-3'
							onClick={exportExcelFunction}>
							<i className='fas fa-file-alt'></i>{' '}
							{translator('Reports-PrintExclBtn')}
						</button> */}
          </div>
          {isLoading && (
            <div className="dashboard-card-spinner">
              <SpinnerLoading />
            </div>
          )}

          {!isLoading && (
            <RemoteAll
              data={posList}
              page={pagination.pageIndex + 1}
              sizePerPage={pagination.pageSize}
              noDataIndication={translator("No-Data")}
              totalSize={pagination.totalCount}
              onTableChange={handleTableChange}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        handleHide={handleHideDeleteModal}
        title={translator("POS-DeleteModal-Title")}
        text={translator("POS-DeleteModal-Text")}
        confirmDelete={confirmDelete}
      />
    </div>
  );
};
export default Index;
